/**
 * ClassificationRulesPanel.tsx — in-app "Classification rules" reference.
 * ─────────────────────────────────────────────────────────────────────────────
 * Renders the ownership/sensitivity/privacy/regulatory rulebook + the "why
 * unknown" explainer straight from dg_config.classification_rules, so any SME
 * sees exactly how values are decided. Config-driven; falls back to the current
 * rulebook if the block is absent, so it works before any SME edit.
 */
import React from "react";

const C = {
  ink: "#0f1923", primary: "#1e2d3d", muted: "#64748b", label: "#2c3e52",
  brand: "#1e3a6e", brandLight: "#dde7f5", brandMid: "#90aed0",
  border: "#c8d4e0", card: "#fff", bg: "#f4f6fa", amberBg: "#fff7ed", amber: "#9a3412",
};

export interface RulebookRule { when: string; value: string }
export interface RulebookSection { id?: string; title: string; note?: string; rules: RulebookRule[] }
export interface Rulebook {
  label?: string; intro?: string; sections: RulebookSection[];
  unknowns?: { title?: string; body?: string; process?: string[] };
}

const FALLBACK: Rulebook = {
  label: "Classification Rules",
  intro: "How the assistant assigns Ownership, Sensitivity, Privacy and Regulatory values. A value is set automatically only when a rule or explicit metadata signal applies; otherwise it is left as a safe default with low confidence for a reviewer to confirm.",
  sections: [
    { title: "Ownership", note: "Evaluated first.", rules: [
      { when: "System of record is an Accenture-operated system or catalogue", value: "Accenture" },
      { when: "System of record is a client-operated system", value: "Client" },
      { when: "System of record is unknown or not listed", value: "Unknown" },
      { when: "Pure system/audit metadata with no business owner", value: "Not Applicable" }] },
    { title: "Sensitivity", note: "Matched by field role; falls back to \"Unknown\" when no rule matches.", rules: [
      { when: "Record identifiers / lookup keys that link records", value: "Highly Confidential" },
      { when: "Contract-structural fields (category, practice, classification, pricing)", value: "Confidential" },
      { when: "Audit timestamps (…date, …timestamp, …dttm)", value: "Not Applicable" },
      { when: "Counter / sequence / version fields", value: "Unrestricted" },
      { when: "Free-text described as \"may contain PII\"", value: "Confidential" },
      { when: "None of the above match", value: "Unknown (safe default — review)" }] },
    { title: "Privacy category", note: "Encodes business confidentiality, not only personal-data privacy.", rules: [
      { when: "Not personal data and low/none sensitivity hint", value: "Not Applicable" },
      { when: "Sensitivity is Confidential or Highly Confidential (bridge)", value: "Business Confidential" },
      { when: "Signals indicate personal/health/financial/educational data", value: "PII / PHI / Financial / Educational" },
      { when: "Reference or code data", value: "Not Applicable (codes are not PII)" }] },
    { title: "Regulatory framework", note: "Applied only where category and jurisdiction both require it.", rules: [
      { when: "PII and EU jurisdiction", value: "GDPR" },
      { when: "PHI and US jurisdiction", value: "HIPAA" },
      { when: "Financial and US jurisdiction", value: "GLBA" },
      { when: "PII and California", value: "CCPA" },
      { when: "No applicable category/jurisdiction", value: "Not Applicable" }] },
  ],
  unknowns: {
    title: "Why a value is \"Unknown\" or \"Not Applicable\"",
    body: "A value is left as a safe default with low confidence when no rule matches and the source metadata carries no explicit signal. The low confidence score is the machine-readable \"please confirm\" — not an error.",
    process: [
      "Filter to \"Needs review\" or \"Low confidence\" in the console.",
      "Read the one-line \"Why\" on each field.",
      "Confirm with the data source owner, then Approve.",
      "Where a pattern repeats, tighten it into a rule so it is no longer Unknown.",
    ],
  },
};

export function resolveClassificationRules(dgConfig: any): Rulebook {
  const cr = dgConfig?.classification_rules;
  return cr && Array.isArray(cr.sections) && cr.sections.length ? cr : FALLBACK;
}

export default function ClassificationRulesPanel({ dgConfig, onClose }: { dgConfig: any; onClose: () => void }) {
  const rb = resolveClassificationRules(dgConfig);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,25,35,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 760, maxWidth: "100%", maxHeight: "88vh", background: C.card, borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,0.3)" }}>
        <div style={{ background: C.brand, padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: "0.9rem", fontWeight: 700, color: "#fff" }}>{rb.label ?? "Classification Rules"}</div>
            <div style={{ fontSize: "0.72rem", color: C.brandLight }}>How ownership, sensitivity, privacy &amp; regulatory values are decided</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "1px solid #ffffff55", color: "#fff", borderRadius: 6, width: 28, height: 28, cursor: "pointer", fontSize: "0.9rem" }}>✕</button>
        </div>

        <div style={{ overflowY: "auto", padding: "18px 20px" }}>
          {rb.intro && <p style={{ fontSize: "0.82rem", color: C.primary, lineHeight: 1.55, margin: "0 0 18px" }}>{rb.intro}</p>}

          {rb.sections.map((sec, i) => (
            <div key={i} style={{ marginBottom: 18 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 7 }}>
                <span style={{ fontSize: "0.74rem", fontWeight: 800, color: C.brand, textTransform: "uppercase", letterSpacing: "0.06em" }}>{sec.title}</span>
                {sec.note && <span style={{ fontSize: "0.72rem", color: C.muted, fontStyle: "italic" }}>{sec.note}</span>}
              </div>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
                {sec.rules.map((r, j) => (
                  <div key={j} style={{ display: "flex", gap: 12, padding: "8px 12px", borderBottom: j < sec.rules.length - 1 ? `1px solid ${C.border}` : "none", background: j % 2 ? C.bg : C.card }}>
                    <span style={{ flex: 1, fontSize: "0.78rem", color: C.primary, lineHeight: 1.45 }}>{r.when}</span>
                    <span style={{ width: 210, flexShrink: 0, fontSize: "0.78rem", fontWeight: 700, color: C.ink }}>{r.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {rb.unknowns && (
            <div style={{ marginTop: 6, padding: "14px 16px", background: C.amberBg, border: `1px solid #fdba74`, borderRadius: 10 }}>
              <div style={{ fontSize: "0.78rem", fontWeight: 800, color: C.amber, marginBottom: 6 }}>{rb.unknowns.title ?? "Unknown values"}</div>
              {rb.unknowns.body && <p style={{ fontSize: "0.78rem", color: C.primary, lineHeight: 1.55, margin: "0 0 10px" }}>{rb.unknowns.body}</p>}
              {rb.unknowns.process && (
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                  {rb.unknowns.process.map((s, i) => <li key={i} style={{ fontSize: "0.78rem", color: C.primary, lineHeight: 1.5, marginBottom: 3 }}>{s}</li>)}
                </ol>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
